// Publish tab — the pre-publish checklist + the Publish action + live
// status (06_PUBLISH_PIPELINE.md). Publishing graduates the active draft
// to a post and commits the whole site atomically; the status walks
// Committing → Building → Live as Cloudflare redeploys.

import { useComposeStore } from "../../store/composeStore";
import { slugify } from "../../lib/post";

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={"pb-check" + (ok ? " pb-check-ok" : "")}>
      <span className="pb-check-mark">{ok ? "✓" : "○"}</span>{label}
    </div>
  );
}

export function PublishPane() {
  const drafts = useComposeStore((s) => s.drafts);
  const activeId = useComposeStore((s) => s.activeId);
  const state = useComposeStore((s) => s.publishState);
  const error = useComposeStore((s) => s.publishError);
  const url = useComposeStore((s) => s.publishedUrl);
  const publishActive = useComposeStore((s) => s.publishActive);
  const unpublish = useComposeStore((s) => s.unpublish);

  const draft = drafts.find((d) => d.id === activeId);
  if (!draft) return <div className="pb-pane" />;

  const hasTitle = !!draft.title.trim();
  const hasBody = !!draft.body.trim();
  const ready = hasTitle && hasBody;
  const isPublished = !!draft.published_slug;
  const slug = draft.published_slug ?? (hasTitle ? slugify(draft.title) : "…");
  const busy = state === "publishing" || state === "building";

  return (
    <div className="pb-pane">
      {isPublished && (
        <div className="pb-status pb-status-published">
          <span className="pb-dot" /> Published
          {url && <a className="pb-view" href={url} target="_blank" rel="noreferrer">view ↗</a>}
        </div>
      )}

      <div className="pb-section">
        <div className="pb-label">Pre-publish checklist</div>
        <Check ok={hasTitle} label="Has a title" />
        <Check ok={hasBody} label="Has content" />
        <div className="pb-slug">slug: <code>{slug}</code></div>
      </div>

      <button className="pb-btn" disabled={!ready || busy} onClick={() => void publishActive()}>
        {state === "publishing" ? "Committing…"
          : state === "building" ? "Building…"
          : isPublished ? "Update published post"
          : "Publish"}
      </button>

      {state === "building" && (
        <div className="pb-building">
          <span className="pb-spin" /> Cloudflare is deploying your site… {url && <a href={url} target="_blank" rel="noreferrer">peek ↗</a>}
        </div>
      )}
      {state === "live" && (
        <div className="pb-live">
          Live ✓ {url && <a href={url} target="_blank" rel="noreferrer">view on site ↗</a>}
        </div>
      )}
      {state === "error" && error && <div className="pb-error">{error}</div>}

      {isPublished && !busy && (
        <button className="pb-unpub" onClick={() => { if (confirm("Remove this post from your public site?")) void unpublish(draft.id); }}>
          unpublish
        </button>
      )}

      <div className="pb-note">
        Publishing commits finished HTML to your public repo; Cloudflare redeploys in ~30–60s.
        Your draft stays here so you can keep editing and republish.
      </div>

      <style>{`
        .pb-pane { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); height: 100%; overflow: auto; }
        .pb-status { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }
        .pb-status-published { color: var(--teal); }
        .pb-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--teal); }
        .pb-view { margin-left: auto; font-size: 12px; }
        .pb-section { background: var(--paper); border: 1px solid var(--line); border-radius: var(--radius-1); padding: var(--space-3); }
        .pb-label { font-family: var(--font-mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-faint); margin-bottom: var(--space-2); }
        .pb-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink-faint); padding: 3px 0; }
        .pb-check-ok { color: var(--ink); }
        .pb-check-mark { color: var(--ink-faint); width: 14px; }
        .pb-check-ok .pb-check-mark { color: var(--teal); }
        .pb-slug { font-size: 12px; color: var(--ink-soft); margin-top: 8px; }
        .pb-slug code { font-family: var(--font-mono); background: var(--paper-sunk); padding: 1px 6px; border-radius: 4px; }
        .pb-btn { font-family: var(--font-sans); font-size: 14px; font-weight: 600; padding: 11px; border-radius: var(--radius-1); border: 1px solid var(--teal); background: var(--teal); color: #fff; cursor: pointer; }
        .pb-btn:hover { background: var(--teal-bright); }
        .pb-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pb-building, .pb-live { font-size: 12px; color: var(--ink-soft); display: flex; align-items: center; gap: 6px; }
        .pb-live { color: var(--teal); font-weight: 600; }
        .pb-spin { width: 10px; height: 10px; border: 2px solid var(--line-strong); border-top-color: var(--teal); border-radius: 50%; animation: pbspin 0.8s linear infinite; }
        @keyframes pbspin { to { transform: rotate(360deg); } }
        .pb-error { font-size: 12px; color: var(--rust); background: var(--rust-bg); border: 1px solid rgba(180,80,42,0.3); border-radius: var(--radius-1); padding: 8px 10px; user-select: text; }
        .pb-unpub { align-self: flex-start; font-family: var(--font-mono); font-size: 11px; color: var(--ink-faint); background: none; border: none; text-decoration: underline; cursor: pointer; }
        .pb-unpub:hover { color: var(--rust); }
        .pb-note { font-size: 11px; line-height: 1.5; color: var(--ink-faint); margin-top: auto; }
      `}</style>
    </div>
  );
}
